/**
 * Collapse a list of deal docs down to one card per brand.
 *
 * Duplicates occur when an orphaned `dealData` doc — left behind by a retailer
 * that was re-IDed or renamed (the processor only purges retailerIds present in
 * the latest scrape) — lingers in Firestore next to the current one. The two
 * share a brand name/slug but have different doc ids, so both render.
 *
 * We key by retailerSlug (the canonical, URL-facing identifier), keep the entry
 * with the highest discount, and borrow a logo from a sibling if the winner
 * happens to be the logo-less one.
 *
 * @param {Array<Object>} deals
 * @returns {Array<Object>} one deal per brand
 */
export function dedupeDealsByBrand(deals) {
    const keyOf = (d) =>
        (d.retailerSlug || d.retailerName || d.retailerId || d.id || '')
            .toString()
            .trim()
            .toLowerCase();

    const hasLogos = (d) => Array.isArray(d.retailerLogoUrls) && d.retailerLogoUrls.length > 0;

    const byBrand = new Map();

    for (const deal of deals) {
        const key = keyOf(deal);
        if (!key) continue;

        const existing = byBrand.get(key);
        if (!existing) {
            byBrand.set(key, deal);
            continue;
        }

        const winner = (deal.discount || 0) > (existing.discount || 0) ? deal : existing;
        const loser = winner === deal ? existing : deal;

        // Ensure the surviving card keeps a logo if either duplicate had one.
        if (!hasLogos(winner) && hasLogos(loser)) {
            byBrand.set(key, { ...winner, retailerLogoUrls: loser.retailerLogoUrls });
        } else {
            byBrand.set(key, winner);
        }
    }

    return Array.from(byBrand.values());
}
